import { useState, useEffect, useRef } from 'react';
import { Search, FileText, Target, Truck, Calendar, File, X, Bell, BookOpen, PenSquare, Share2, Workflow } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { ApiResponse, SearchResponse, SearchResult } from '@/types';

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: typeof Search; color: string }> = {
  activities: { label: 'Aktivitäten', icon: Target, color: 'text-accent' },
  campaigns: { label: 'Kampagnen', icon: FileText, color: 'text-muted' },
  suppliers: { label: 'Zulieferer', icon: Truck, color: 'text-success' },
  plans: { label: 'Pläne', icon: Calendar, color: 'text-warning' },
  documents: { label: 'Dokumente', icon: File, color: 'text-muted' },
};

const QUICK_LINKS: Array<{ label: string; path: string; icon: typeof Search; color: string }> = [
  { label: 'Composer', path: '/composer', icon: PenSquare, color: 'text-foreground' },
  { label: 'Social Hub', path: '/social', icon: Share2, color: 'text-muted' },
  { label: 'Kalender', path: '/calendar', icon: Calendar, color: 'text-accent' },
  { label: 'Automation', path: '/automation', icon: Workflow, color: 'text-muted' },
  { label: 'Posteingang', path: '/notifications', icon: Bell, color: 'text-warning' },
  { label: 'Onboarding', path: '/onboarding', icon: BookOpen, color: 'text-foreground' },
  { label: 'Kampagnen', path: '/campaigns', icon: FileText, color: 'text-muted' },
  { label: 'Aktivitäten', path: '/activities', icon: Target, color: 'text-accent' },
];

export function SearchDialog({ open, onClose, onNavigate }: SearchDialogProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('lc_recent_searches');
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (Array.isArray(parsed)) setRecentSearches(parsed.filter((item): item is string => typeof item === 'string').slice(0, 5));
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({});
    }
  }, [open]);

  useEffect(() => {
    setResults({});
    setError('');
    if (!open || query.trim().length < 2) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get<ApiResponse<SearchResponse>>(`/search?q=${encodeURIComponent(query.trim())}&limit=5`, { signal: controller.signal });
        if (!res.success) throw new Error(res.error || 'Suche fehlgeschlagen.');
        if (!controller.signal.aborted) setResults(res.data ?? {});
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Suche fehlgeschlagen.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [open, query]);

  const handleInputChange = (value: string) => {
    setQuery(value);
  };

  const handleSelect = (path: string) => {
    if (query.trim()) {
      const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 5);
      setRecentSearches(updated);
      try { localStorage.setItem('lc_recent_searches', JSON.stringify(updated)); } catch { /* Search still works without storage. */ }
    }
    onNavigate(path);
    onClose();
  };

  const hasResults = Object.values(results).some((arr) => arr && arr.length > 0);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent
        showClose={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          event.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => { event.preventDefault(); previousFocusRef.current?.focus(); }}
        className="top-[15vh] max-h-[80dvh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">Suche</DialogTitle>
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search size={18} className="shrink-0 text-muted" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Suche"
            placeholder="Suche nach Aktivitäten, Kampagnen, Zulieferern..."
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            className="h-14 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted"
          />
          {query && (
            <button type="button" aria-label="Suche leeren" onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-muted hover:text-foreground">
              <X size={16} />
            </button>
          )}
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted sm:inline">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div role="status" aria-label="Suche läuft" className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
            </div>
          )}

          {!loading && !query && (
            <div className="mb-1">
              <p className="px-2 py-1.5 text-xs font-medium text-muted">Schnellzugriff</p>
              {QUICK_LINKS.map(({ label, path, icon: Icon, color }) => (
                <button
                  key={path}
                  onClick={() => handleSelect(path)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-card-hover"
                >
                  <Icon size={16} className={cn('shrink-0', color)} />
                  <span className="truncate text-sm font-medium text-foreground">{label}</span>
                </button>
              ))}
            </div>
          )}

          {error && <p role="alert" className="px-3 py-6 text-sm text-danger">{error}</p>}

          {!loading && !error && !hasResults && query.trim().length >= 2 && (
            <p className="py-8 text-center text-sm text-muted">Keine Ergebnisse für "{query}"</p>
          )}

          {!loading && !query && recentSearches.length > 0 && (
            <div>
              <p className="px-2 py-1.5 text-xs font-medium text-muted">Letzte Suchen</p>
              {recentSearches.map((s) => (
                <button
                  key={s}
                  onClick={() => handleInputChange(s)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted hover:bg-card-hover"
                >
                  <Search size={14} className="text-muted" /> {s}
                </button>
              ))}
            </div>
          )}

          {!loading && hasResults && Object.entries(results).map(([type, items]) => {
            if (!items || items.length === 0) return null;
            const config = TYPE_CONFIG[type];
            if (!config) return null;
            const Icon = config.icon;

            return (
              <div key={type} className="mb-1">
                <p className="px-2 py-1.5 text-xs font-medium text-muted">{config.label}</p>
                {items.map((item: SearchResult) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item._path)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-card-hover"
                  >
                    <Icon size={16} className={cn('shrink-0', config.color)} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {item.title || item.name || item.id}
                      </p>
                      {item.status && (
                        <p className="text-xs text-muted">{item.status}{item.type ? ` · ${item.type}` : ''}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
